import { Box, Typography, Paper, CircularProgress, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { useThreads } from '@lingaai/shared-hooks';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';
import { formatDateTime } from '@lingaai/shared-utils';

interface ThreadsSectionProps {
  nodeId: string;
}

function getBodyPreview(bodyHtml?: string): string {
  if (!bodyHtml) return '';
  const div = document.createElement('div');
  div.innerHTML = bodyHtml;
  const text = div.textContent || div.innerText || '';
  return text.slice(0, 100).trim();
}

export function ThreadsSection({ nodeId }: ThreadsSectionProps) {
  const { data: threads = [], isLoading, error } = useThreads(nodeId);

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Failed to load thread messages</Typography>
      </Box>
    );
  }

  if (threads.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">No thread messages</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {threads.map((thread) => (
          <Accordion key={thread.id} defaultExpanded={false}>
            <AccordionSummary
              expandIcon={<ChevronDown size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />}
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%', pr: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <Typography variant="body2" fontWeight={500}>
                    {thread.from_email}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 2 }}>
                    {formatDateTime(thread.sent_at)}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={500}>
                  {thread.subject}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {getBodyPreview(thread.body_html)}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    To:
                  </Typography>
                  <Typography variant="body2">{thread.to_emails.join(', ')}</Typography>
                </Box>

                {thread.cc_emails && thread.cc_emails.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Cc:
                    </Typography>
                    <Typography variant="body2">{thread.cc_emails.join(', ')}</Typography>
                  </Box>
                )}

                <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  {thread.body_html ? (
                    <div
                      dangerouslySetInnerHTML={{ __html: thread.body_html }}
                      style={{
                        fontSize: '14px',
                        lineHeight: '1.6',
                        color: 'inherit',
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No content
                    </Typography>
                  )}
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Paper>
  );
}
